import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import {
  NotificationPreferenceAudience,
  NotificationPreferenceScope,
} from "generated-graphql/graphql";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useCallback, useState } from "react";

import {
  HAS_OPEN_NOTIFICATIONS_TO_DELIVER,
  MY_NOTIFICATION_PREFERENCE_CATALOG,
  RESET_MY_NOTIFICATION_PREFERENCES,
  RESET_ORGANIZATION_NOTIFICATION_PREFERENCES,
  SET_NOTIFICATION_PREFERENCES,
} from "./notificationPreferenceDocuments";
import {
  browserTimeZone,
  buildSavePayload,
  draftValue,
  newlyDeliveringKinds,
  NOTIFICATION_CHANNEL_EMAIL,
  useNotificationPreferenceDraft,
  type DeliverySection,
  type DraftParentSwitch,
  type DraftTimeZoneEntry,
  type NotificationPreferenceDraftController,
} from "./useNotificationPreferenceDraft";

/**
 * State and behaviour for the notification preferences page: which sections are
 * open, both tabs' catalogs and drafts, and the write path behind Save, Reset and
 * the preset.
 *
 * Split out of the page component so the file that renders the layout is about
 * layout. These four hooks are the page's moving parts, and keeping them together
 * makes the one thing they share obvious — every one of them exists so a reader's
 * unsaved edits survive a tab switch, a failed refetch, or a reload.
 */

/**
 * The organization a write applies to. Null is every organization, which only the
 * Encamp alerts tab offers; undefined is not known yet, and nothing writes.
 */
export type PreferenceTarget = string | null | undefined;

/** Said when a write landed but the page could not catch up with it. */
export const REFRESH_FAILED_COPY =
  "Your change was saved, but this page could not be refreshed. Reload to see the current values.";

function useRefreshAfterWrite(setRefreshing: (value: boolean) => void) {
  const alerts = useAlerts();
  return useCallback(
    async (refetch: () => Promise<unknown>): Promise<boolean> => {
      setRefreshing(true);
      try {
        await refetch();
        return true;
      } catch {
        alerts.error(REFRESH_FAILED_COPY);
        return false;
      } finally {
        setRefreshing(false);
      }
    },
    [alerts, setRefreshing]
  );
}

/**
 * Which sections are open, remembered across visits.
 *
 * Everything starts expanded: a reader who came here to change one notification
 * should see the notifications, not five closed drawers to hunt through. After that
 * their own choice wins, because a collapse a page forgets is a page that argues
 * with them every time they arrive.
 *
 * Stored as an override map rather than a list of open sections, so a group that
 * ships later is expanded by default too instead of silently arriving collapsed.
 */
export function useExpandedSections() {
  const [collapsedByGroup, setCollapsedByGroup] = useLocalStorageState<Record<string, boolean>>(
    "notification-preferences-collapsed-sections",
    { defaultValue: {} }
  );

  const isExpanded = useCallback(
    (groupId: string) => !collapsedByGroup[groupId],
    [collapsedByGroup]
  );

  const toggle = useCallback(
    (groupId: string) => {
      setCollapsedByGroup((current) => ({ ...current, [groupId]: !current[groupId] }));
    },
    [setCollapsedByGroup]
  );

  return { isExpanded, toggle };
}

/**
 * Both tabs' catalogs and drafts, and which of them the visible tab is using.
 *
 * One hook because the two queries are the same question asked at two layers and
 * their drafts have to outlive a tab switch — a draft owned by the panel would throw
 * away unsaved edits the moment the reader looked at the other tab.
 */
export function usePreferenceCatalogs({
  tenantId,
  scope,
  canManageOrganizationDefaults,
  skipMine = false,
}: {
  tenantId: string | undefined;
  scope: NotificationPreferenceScope;
  canManageOrganizationDefaults: boolean;
  /** True for staff, whose own preferences live on the Encamp alerts tab instead. */
  skipMine?: boolean;
}) {
  const myQuery = useQuery(MY_NOTIFICATION_PREFERENCE_CATALOG, {
    variables: {
      tenantId: tenantId ?? "",
      scope: NotificationPreferenceScope.My,
      audience: NotificationPreferenceAudience.Customer,
    },
    skip: !tenantId || skipMine,
  });
  const organizationQuery = useQuery(MY_NOTIFICATION_PREFERENCE_CATALOG, {
    variables: {
      tenantId: tenantId ?? "",
      scope: NotificationPreferenceScope.OrgDefault,
      audience: NotificationPreferenceAudience.Customer,
    },
    skip: !tenantId || !canManageOrganizationDefaults,
  });

  const myCatalog = myQuery.data?.myNotificationPreferenceCatalog;
  const organizationCatalog = organizationQuery.data?.myNotificationPreferenceCatalog;

  // Both drafts live here rather than inside the panel, so switching tabs cannot
  // throw away edits the reader has not saved yet.
  const myDraft = useNotificationPreferenceDraft(myCatalog);
  const organizationDraft = useNotificationPreferenceDraft(organizationCatalog);

  /*
    The permission is part of the question, not just of whether the tab is offered.

    This component survives a tenant change. A reader could leave it on the
    organization tab and come back under a tenant where they cannot manage defaults:
    the tab strip is hidden and the organization query is skipped, but a selection
    made on `scope` alone would still point at that skipped query — leaving the page
    loading forever with no visible way back to their own preferences.

    Derived rather than reset through an effect, so there is no first paint in which
    the two disagree.
  */
  const onOrganizationTab =
    scope === NotificationPreferenceScope.OrgDefault && canManageOrganizationDefaults;
  /*
    The scope everything else must use: the heading, the panel, and above all the
    save. Reading raw `scope` while the data came from the other tab is worse than the
    stranded loading state it replaced — a save would have written ORG_DEFAULT rows out
    of the reader's personal draft.
  */
  const effectiveScope = onOrganizationTab
    ? NotificationPreferenceScope.OrgDefault
    : NotificationPreferenceScope.My;
  const activeCatalog = onOrganizationTab ? organizationCatalog : myCatalog;
  const activeController = onOrganizationTab ? organizationDraft : myDraft;
  const activeQuery = onOrganizationTab ? organizationQuery : myQuery;

  const refetchBoth = useCallback(async () => {
    // An organization default is a layer under every member's view, so a change
    // to it moves the reader's own resolved values too — including this reader's.
    // Refetching only the tab that was saved would leave the other one stale in a
    // way the reader could see by clicking once.
    if (!skipMine) {
      await myQuery.refetch();
    }
    if (canManageOrganizationDefaults) {
      await organizationQuery.refetch();
    }
  }, [canManageOrganizationDefaults, myQuery, organizationQuery, skipMine]);

  return {
    myQuery,
    organizationCatalog,
    onOrganizationTab,
    effectiveScope,
    activeCatalog,
    activeController,
    activeQuery,
    refetchBoth,
  };
}

/**
 * The zone entry a save should carry, or null when it should not.
 *
 * Only when the reader moved a schedule, and only on their own tab: cadence is
 * measured in this zone, so a reader who has never stored one would silently be on
 * Eastern.
 */
export function timeZoneEntryForSave(
  dirty: readonly { channel: string; cadence?: string }[],
  channelParentSwitches: readonly DraftParentSwitch[] | undefined,
  draft: Parameters<typeof draftValue>[0],
  scope: NotificationPreferenceScope
): DraftTimeZoneEntry | null {
  if (scope !== NotificationPreferenceScope.My) {
    return null;
  }
  const movedASchedule = dirty.some(
    (toggle) => toggle.channel === NOTIFICATION_CHANNEL_EMAIL && toggle.cadence
  );
  const emailParent = channelParentSwitches?.find(
    (parent) => parent.channel === NOTIFICATION_CHANNEL_EMAIL
  );
  if (!movedASchedule || !emailParent) {
    return null;
  }
  return {
    kind: emailParent.kind,
    channel: emailParent.channel,
    enabled: draftValue(draft, emailParent.kind, emailParent.channel),
    timeZone: browserTimeZone(),
  };
}

/**
 * The page's three writes, each reporting an error the same way.
 *
 * Grouped because they share exactly one thing — every failure becomes an alert
 * rather than an unhandled rejection — and because keeping them beside the
 * handlers that call them pushed the hook that owns those handlers past the
 * function-length cap.
 */
function usePreferenceMutations() {
  const alerts = useAlerts();
  const onError = useCallback(
    (error: { message: string }) => alerts.error(error.message),
    [alerts]
  );

  const [setPreferences, { loading: saving }] = useMutation(SET_NOTIFICATION_PREFERENCES, {
    onError,
  });
  const [resetPreferences, { loading: resetting }] = useMutation(
    RESET_MY_NOTIFICATION_PREFERENCES,
    { onError }
  );
  const [resetOrganizationPreferences, { loading: applyingPreset }] = useMutation(
    RESET_ORGANIZATION_NOTIFICATION_PREFERENCES,
    { onError }
  );

  return {
    alerts,
    setPreferences,
    saving,
    resetPreferences,
    resetting,
    resetOrganizationPreferences,
    applyingPreset,
  };
}

/**
 * The un-mute warning: whether this save would hand the reader (or, on the
 * organization tab, its members) conditions that are already open.
 *
 * A muted alert does not stop its condition being recorded — the notification is
 * still written, it just reaches nobody — so switching one back on delivers every
 * such condition still true at the next sweep.
 */
function useUnmuteWarning({
  tenantId,
  scope,
  activeController,
  sections,
  channelParentSwitches,
}: {
  tenantId: PreferenceTarget;
  scope: NotificationPreferenceScope;
  activeController: NotificationPreferenceDraftController;
  sections: readonly DeliverySection[] | undefined;
  channelParentSwitches: readonly DraftParentSwitch[] | undefined;
}) {
  const [warningOpen, setWarningOpen] = useState(false);
  const [checkOpenNotifications, { loading: checkingOpenNotifications }] = useLazyQuery(
    HAS_OPEN_NOTIFICATIONS_TO_DELIVER,
    { fetchPolicy: "network-only" }
  );

  const closeWarning = useCallback(() => setWarningOpen(false), []);

  const openWarningIfNeeded = useCallback(async (): Promise<boolean> => {
    // Open conditions belong to one organization, so there is nothing to ask for all of them.
    if (!tenantId || !sections || !channelParentSwitches) {
      return false;
    }
    const kinds = newlyDeliveringKinds(
      sections,
      activeController.draft,
      activeController.snapshot,
      channelParentSwitches
    );
    if (kinds.length === 0) {
      return false;
    }

    try {
      const { data } = await checkOpenNotifications({ variables: { tenantId, kinds, scope } });
      if (!data?.hasOpenNotificationsToDeliver) {
        return false;
      }
    } catch {
      return false;
    }

    setWarningOpen(true);
    return true;
  }, [activeController, channelParentSwitches, checkOpenNotifications, scope, sections, tenantId]);

  return { warningOpen, checkingOpenNotifications, closeWarning, openWarningIfNeeded };
}

/**
 * The Save button's whole behaviour: the write, and the warning that can interrupt
 * it.
 *
 * Its own hook for the reason {@link useResetActions} is — the two are the page's
 * write paths and neither belongs inside the other — and because the gate is the
 * only place where "what the button runs" and "what actually writes" are two
 * different functions. Keeping them adjacent is what makes that visible.
 */
function useSaveAction({
  tenantId,
  scope,
  activeController,
  sections,
  channelParentSwitches,
  setPreferences,
  refetchBoth,
  refreshAfterWrite,
}: {
  tenantId: PreferenceTarget;
  scope: NotificationPreferenceScope;
  activeController: NotificationPreferenceDraftController;
  sections: readonly DeliverySection[] | undefined;
  channelParentSwitches: readonly DraftParentSwitch[] | undefined;
  setPreferences: ReturnType<typeof usePreferenceMutations>["setPreferences"];
  refetchBoth: () => Promise<void>;
  refreshAfterWrite: (refetch: () => Promise<unknown>) => Promise<boolean>;
}) {
  const alerts = useAlerts();
  const { warningOpen, checkingOpenNotifications, closeWarning, openWarningIfNeeded } =
    useUnmuteWarning({
      tenantId,
      scope,
      activeController,
      sections,
      channelParentSwitches,
    });

  const savePreferences = useCallback(async () => {
    if (tenantId === undefined || !activeController.isDirty) {
      return;
    }
    const result = await setPreferences({
      variables: {
        tenantId,
        input: buildSavePayload(
          activeController.dirty,
          scope,
          timeZoneEntryForSave(
            activeController.dirty,
            channelParentSwitches,
            activeController.draft,
            scope
          )
        ),
      },
    });
    if (!result.data) {
      return;
    }
    if (!(await refreshAfterWrite(refetchBoth))) {
      return;
    }
    alerts.success("Notification preferences saved.");
  }, [
    activeController,
    alerts,
    channelParentSwitches,
    refetchBoth,
    refreshAfterWrite,
    scope,
    setPreferences,
    tenantId,
  ]);

  const handleSave = useCallback(async () => {
    if (await openWarningIfNeeded()) {
      return;
    }
    await savePreferences();
  }, [openWarningIfNeeded, savePreferences]);

  const confirmSaveAfterWarning = useCallback(async () => {
    await savePreferences();
    closeWarning();
  }, [closeWarning, savePreferences]);

  return {
    handleSave,
    checkingOpenNotifications,
    warningOpen,
    closeWarning,
    confirmSaveAfterWarning,
  };
}

/** The two actions that discard preference rows: a personal reset and the org preset. */
function useResetActions({
  tenantId,
  audience,
  resetPreferences,
  resetOrganizationPreferences,
  refetchBoth,
  refetchMine,
  refreshAfterWrite,
  closeReset,
  closePreset,
}: {
  tenantId: PreferenceTarget;
  audience: NotificationPreferenceAudience;
  resetPreferences: ReturnType<typeof usePreferenceMutations>["resetPreferences"];
  resetOrganizationPreferences: ReturnType<
    typeof usePreferenceMutations
  >["resetOrganizationPreferences"];
  refetchBoth: () => Promise<void>;
  refetchMine: () => Promise<unknown>;
  refreshAfterWrite: (refetch: () => Promise<unknown>) => Promise<boolean>;
  closeReset: () => void;
  closePreset: () => void;
}) {
  const alerts = useAlerts();

  const handleReset = useCallback(async () => {
    if (tenantId === undefined) {
      return;
    }
    const result = await resetPreferences({ variables: { tenantId, audience } });
    closeReset();
    if (!result.data) {
      return;
    }
    // Only the reader's own rows moved, so the organization tab is still accurate.
    if (!(await refreshAfterWrite(refetchMine))) {
      return;
    }
    alerts.success("Your notification preferences were reset.");
  }, [alerts, audience, closeReset, refetchMine, refreshAfterWrite, resetPreferences, tenantId]);

  const handleApplyPreset = useCallback(async () => {
    if (!tenantId) {
      closePreset();
      return;
    }
    // A delete, not a bulk write of the recommended values. Those values are what an
    // organization with no rows of its own already resolves to, so writing them would
    // change nothing today and then freeze this organization out of every later change
    // Encamp makes to its own recommendations — invisibly, because the page looks
    // identical either way.
    const result = await resetOrganizationPreferences({ variables: { tenantId } });
    closePreset();
    if (!result.data) {
      return;
    }
    if (!(await refreshAfterWrite(refetchBoth))) {
      return;
    }
    alerts.success("Your organization's defaults are back to Encamp's recommended posture.");
  }, [alerts, closePreset, refetchBoth, refreshAfterWrite, resetOrganizationPreferences, tenantId]);

  return { handleReset, handleApplyPreset };
}

export function usePreferenceActions({
  tenantId,
  audience = NotificationPreferenceAudience.Customer,
  scope,
  activeController,
  sections,
  channelParentSwitches,
  refetchBoth,
  refetchMine,
  closeReset,
  closePreset,
}: {
  tenantId: PreferenceTarget;
  audience?: NotificationPreferenceAudience;
  scope: NotificationPreferenceScope;
  activeController: NotificationPreferenceDraftController;
  sections: readonly DeliverySection[] | undefined;
  /** The active tab's channel switches, which is where the zone is stored. */
  channelParentSwitches: readonly DraftParentSwitch[] | undefined;
  refetchBoth: () => Promise<void>;
  /** Only the reader's own tab: a personal reset cannot move an organization default. */
  refetchMine: () => Promise<unknown>;
  closeReset: () => void;
  closePreset: () => void;
}) {
  const {
    setPreferences,
    saving,
    resetPreferences,
    resetting,
    resetOrganizationPreferences,
    applyingPreset,
  } = usePreferenceMutations();

  /**
   * True from the moment a write leaves until its refetch has settled.
   *
   * Every switch on the page is blocked for that whole window, which is a
   * deliberate choice over the alternative of merging edits made mid-flight. The
   * draft is re-seeded from whatever the refetch returns, so an edit made while a
   * save was in the air was silently thrown away and the toast still said saved.
   * Preserving it instead would mean diffing the reader's edit against a baseline
   * that may already contain the value they just saved — a flip made and unmade
   * during the flight becomes indistinguishable from no change, and would be
   * dropped anyway. Blocking loses nothing except a second of interactivity, and
   * it cannot lose an edit.
   */
  const [refreshing, setRefreshing] = useState(false);
  const refreshAfterWrite = useRefreshAfterWrite(setRefreshing);

  const {
    handleSave,
    checkingOpenNotifications,
    warningOpen,
    closeWarning,
    confirmSaveAfterWarning,
  } = useSaveAction({
    tenantId,
    scope,
    activeController,
    sections,
    channelParentSwitches,
    setPreferences,
    refetchBoth,
    refreshAfterWrite,
  });

  const mutating = saving || resetting || applyingPreset || refreshing || checkingOpenNotifications;

  const { handleReset, handleApplyPreset } = useResetActions({
    tenantId,
    audience,
    resetPreferences,
    resetOrganizationPreferences,
    refetchBoth,
    refetchMine,
    refreshAfterWrite,
    closeReset,
    closePreset,
  });

  return {
    mutating,
    saving: saving || checkingOpenNotifications,
    resetting,
    applyingPreset,
    handleSave,
    handleReset,
    handleApplyPreset,
    unmuteWarningOpen: warningOpen,
    closeUnmuteWarning: closeWarning,
    confirmSaveAfterWarning,
  };
}
